import { env } from '../config/env';

import { createHttpClient, type HttpClient } from './http-client';

/**
 * Application-wide transport. Feature hooks import this instance; feature
 * *services* accept an `HttpClient` argument so they stay unit-testable.
 */
let unauthorizedHandler: (() => void) | null = null;

/** Registered once by the auth provider so a 401 ends the session centrally. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * Supplies the access token. Registered by the auth layer rather than imported,
 * so this module stays free of any dependency on MSAL — and so tests can drive
 * the transport without an identity provider.
 */
let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(provider: (() => Promise<string | null>) | null): void {
  tokenProvider = provider;
}

/**
 * How a request proves who is calling.
 *
 * Entra when configured, otherwise the `X-Debug-User` development bypass. The
 * two are mutually exclusive by construction: `env.debugUser` is empty whenever
 * Entra is configured, so a stale header can never shadow a real token — the
 * backend tries its dev provider first and would otherwise silently prefer it.
 */
async function authHeaders(): Promise<Record<string, string>> {
  if (tokenProvider) {
    const token = await tokenProvider();
    if (token) return { Authorization: `Bearer ${token}` };
  }
  return env.debugUser ? { 'X-Debug-User': env.debugUser } : {};
}

export const apiClient: HttpClient = createHttpClient({
  baseUrl: env.apiBaseUrl,
  onUnauthorized: () => unauthorizedHandler?.(),
  defaultHeaders: authHeaders,
});
